import { lazy } from 'react'
import { Navigate } from 'react-router'

import { ErrorPageNotFound } from '../components/ErrorPage'
import {
  ROUTE_PARAM_EMAIL_ACCOUNT_ID,
  ROUTE_PARAM_IDWELL_TO_IDWELL_CONNECTION_UUID,
  ROUTE_PATH_LIST_SETTINGS,
  ROUTE_UI_SECTION_NAME,
  WEBAPP_IDWELL_TO_IDWELL_INBOUND_PATH,
  WEBAPP_IDWELL_TO_IDWELL_OUTBOUND_PATH,
} from '../constants/route'
import { isCalendarSettingsAvailable } from '../utils/calendar'
import {
  getEmailIntegrationSettingsWebappPath,
  getIdwellToIdwellSettingsWebappPath,
  getIntegerParamGuardLoader,
  getPermissionGuardLoader,
  getPermissionProtectedRouteLoader,
  getSettingsPageGuardLoader,
  getUuidParamGuardLoader,
} from '../utils/route'

const AdvancedSettings = lazy(() =>
  import('../components/AdvancedSettings').then(m => ({
    default: m.AdvancedSettings,
  }))
)

const AutomatedDocumentRecognitionSettings = lazy(() =>
  import('../components/AutomatedDocumentRecognitionSettings').then(m => ({
    default: m.AutomatedDocumentRecognitionSettings,
  }))
)

const CalendarSettings = lazy(() =>
  import('../components/CalendarSettings').then(m => ({
    default: m.CalendarSettings,
  }))
)

const CompanyEpostBilling = lazy(() =>
  import('../components/EpostBillingSettings').then(m => ({
    default: m.EpostBillingSettings,
  }))
)
const ColorThemeSettings = lazy(() =>
  import('../components/CompanySettings').then(m => ({
    default: m.ColorThemeSettings,
  }))
)
const CompanySettings = lazy(() =>
  import('../components/CompanySettings').then(m => ({
    default: m.CompanySettings,
  }))
)

const ConfigurationSettings = lazy(() =>
  import('../components/ConfigurationSettings').then(m => ({
    default: m.ConfigurationSettings,
  }))
)

const EmailIntegrationSettings = lazy(() =>
  import('../components/EmailIntegrationSettings').then(m => ({
    default: m.EmailIntegrationSettings,
  }))
)

const EmailIntegrationSettingsProfile = lazy(() =>
  import('../components/EmailIntegrationSettings/EmailIntegrationSettingsProfile').then(
    m => ({
      default: m.EmailIntegrationSettingsProfile,
    })
  )
)

const ERPIntegrationSettings = lazy(() =>
  import('../components/ERPIntegrationSettings').then(m => ({
    default: m.ERPIntegrationSettings,
  }))
)

const GoogleDriveIntegrationSettings = lazy(() =>
  import('../components/GoogleDriveIntegrationSettings').then(m => ({
    default: m.GoogleDriveIntegrationSettings,
  }))
)

const IdwellToIdwellSettings = lazy(() =>
  import('../components/IdwellToIdwellSettings').then(m => ({
    default: m.IdwellToIdwellSettings,
  }))
)

const IdwellToIdwellSettingsInboundProfile = lazy(() =>
  import('../components/IdwellToIdwellSettings/IdwellToIdwellSettingsInboundProfile').then(
    m => ({
      default: m.IdwellToIdwellSettingsInboundProfile,
    })
  )
)

const IdwellToIdwellSettingsOutboundProfile = lazy(() =>
  import('../components/IdwellToIdwellSettings/IdwellToIdwellSettingsOutboundProfile').then(
    m => ({
      default: m.IdwellToIdwellSettingsOutboundProfile,
    })
  )
)

const IntegrationProtocolSettings = lazy(() =>
  import('../components/IntegrationProtocolSettings').then(m => ({
    default: m.IntegrationProtocolSettings,
  }))
)

const Labels = lazy(() =>
  import('../components/Labels').then(m => ({
    default: m.Labels,
  }))
)

const ManageAuthorizationsSettings = lazy(() =>
  import('../components/ManageAuthorizationsSettings').then(m => ({
    default: m.ManageAuthorizationsSettings,
  }))
)

const PersonalSettings = lazy(() =>
  import('../components/PersonalSettings').then(m => ({
    default: m.PersonalSettings,
  }))
)

const TariffsLimits = lazy(() =>
  import('../components/TariffsLimits').then(m => ({
    default: m.TariffsLimits,
  }))
)

export function getSettingsRoutes({ availableSections, sections }) {
  return [
    {
      children: [
        {
          element: <Navigate replace to={ROUTE_PATH_LIST_SETTINGS.company} />,
          index: true,
        },
        {
          element: <PersonalSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.personal,
            sectionName: ROUTE_UI_SECTION_NAME.settingsPersonal,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.personal,
        },
        {
          element: <CompanySettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.company,
            sectionName: ROUTE_UI_SECTION_NAME.settingsCompany,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.company,
        },
        {
          element: <GoogleDriveIntegrationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.googleDrive,
            sectionName: ROUTE_UI_SECTION_NAME.settingsGoogleDrive,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.googleDrive,
        },
        {
          element: <ERPIntegrationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.erpIntegration,
            sectionName: ROUTE_UI_SECTION_NAME.settingsErpIntegration,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.erpIntegration,
        },
        {
          element: <IntegrationProtocolSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.integrationProtocol,
            sectionName: ROUTE_UI_SECTION_NAME.settingsIntegrationProtocol,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.integrationProtocol,
        },
        {
          element: <IdwellToIdwellSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            getWebappPath: getIdwellToIdwellSettingsWebappPath,
            path: ROUTE_PATH_LIST_SETTINGS.idwellToIdwell,
            sectionName: ROUTE_UI_SECTION_NAME.settingsIdwellToIdwell,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.idwellToIdwell,
        },
        {
          element: <IdwellToIdwellSettingsInboundProfile />,
          loader: getUuidParamGuardLoader({
            loader: getSettingsPageGuardLoader({
              availableSections,
              getWebappPath: getIdwellToIdwellSettingsWebappPath,
              path: WEBAPP_IDWELL_TO_IDWELL_INBOUND_PATH,
              sectionName: ROUTE_UI_SECTION_NAME.settingsIdwellToIdwell,
              sections,
            }),
            paramName: ROUTE_PARAM_IDWELL_TO_IDWELL_CONNECTION_UUID,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.idwellToIdwellInbound,
        },
        {
          element: <IdwellToIdwellSettingsOutboundProfile />,
          loader: getUuidParamGuardLoader({
            loader: getSettingsPageGuardLoader({
              availableSections,
              getWebappPath: getIdwellToIdwellSettingsWebappPath,
              path: WEBAPP_IDWELL_TO_IDWELL_OUTBOUND_PATH,
              sectionName: ROUTE_UI_SECTION_NAME.settingsIdwellToIdwell,
              sections,
            }),
            paramName: ROUTE_PARAM_IDWELL_TO_IDWELL_CONNECTION_UUID,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.idwellToIdwellOutbound,
        },
        {
          element: <EmailIntegrationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            getWebappPath: getEmailIntegrationSettingsWebappPath,
            sectionName: ROUTE_UI_SECTION_NAME.settingsEmailIntegration,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.emailIntegrationNew,
        },
        {
          element: <EmailIntegrationSettingsProfile />,
          loader: getIntegerParamGuardLoader({
            loader: getSettingsPageGuardLoader({
              availableSections,
              getWebappPath: getEmailIntegrationSettingsWebappPath,
              sectionName: ROUTE_UI_SECTION_NAME.settingsEmailIntegration,
              sections,
            }),
            paramName: ROUTE_PARAM_EMAIL_ACCOUNT_ID,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.emailIntegrationAccount,
        },
        {
          element: <ConfigurationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.configuration,
            sectionName: ROUTE_UI_SECTION_NAME.settingsConfiguration,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.configuration,
        },
        {
          element: <ManageAuthorizationsSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.manageAuth,
            sectionName: ROUTE_UI_SECTION_NAME.settingsManageAuth,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.manageAuth,
        },
        {
          element: <AdvancedSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.advanced,
            sectionName: ROUTE_UI_SECTION_NAME.settingsAdvanced,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.advanced,
        },
        {
          element: <AutomatedDocumentRecognitionSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.automatedDocument,
            sectionName: ROUTE_UI_SECTION_NAME.settingsDocumentRecognition,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.automatedDocument,
        },
        {
          element: <ColorThemeSettings />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsColorTheme,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.colorTheme,
        },
        {
          element: <Labels />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsLabels,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.labels,
        },
        {
          element: <CompanyEpostBilling />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsEpostBilling,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.epostBilling,
        },
        {
          element: <TariffsLimits />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsTariffsLimits,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.tariffs,
        },
        {
          element: <CalendarSettings />,
          loader: () =>
            getPermissionProtectedRouteLoader({
              hasAccess: isCalendarSettingsAvailable({
                availableSections,
                sections,
              }),
            }),
          path: ROUTE_PATH_LIST_SETTINGS.calendar,
        },
      ],
      errorElement: <ErrorPageNotFound />,
    },
  ]
}
